import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getOrdersByBuyer } from '../api/client';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { parseUTC } from '../utils/date';
import { Package, Clock, Check, Truck, ChevronRight, ShoppingBag, X } from 'lucide-react';
import type { Order, OrderStatus } from '../types';
import { UserAvatar } from '../components/UserAvatar';
import { Img } from '../components/Img';
import type { LucideIcon } from 'lucide-react';

const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bgColor: string; Icon: LucideIcon }> = {
  OPEN: { label: 'Open', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  UNSHIPPED: { label: 'Awaiting Shipment', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Package },
  PARTIALLY_SHIPPED: { label: 'Partially Shipped', color: '#F97316', bgColor: '#FFF7ED', Icon: Package },
  LABEL_CREATED: { label: 'Label Created', color: '#8B5CF6', bgColor: '#EDE9FE', Icon: Package },
  SHIPPED: { label: 'Shipped', color: '#3B82F6', bgColor: '#DBEAFE', Icon: Truck },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7', Icon: Check },
  CANCELLED: { label: 'Cancelled', color: '#6B7280', bgColor: '#F3F4F6', Icon: X },
};

function formatDate(dateString: string): string {
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function OrdersPage() {
  useDocumentTitle('Orders');
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    loadOrders();
  }, [page]);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      const data = await getOrdersByBuyer(page, 20);
      setOrders(data.content);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <h1 style={styles.title}>Orders</h1>
          <p style={styles.subtitle}>Track your purchases and order history</p>

          {isLoading ? (
            <div style={styles.loading}>Loading orders...</div>
          ) : error ? (
            <div style={styles.error}>{error}</div>
          ) : orders.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>
                <Package size={48} color={colors.gray[300]} />
              </div>
              <h3 style={styles.emptyTitle}>No orders yet</h3>
              <p style={styles.emptyText}>
                When you win an auction or buy an item, your orders will appear here.
              </p>
            </div>
          ) : (
            <>
              <div style={styles.orderList}>
                {orders.map((order) => {
                  const statusConfig = STATUS_CONFIG[order.status];
                  const imageUrl = order.showThumbnailUrl;
                  const itemCount = order.itemSaleCount || order.itemSales?.length || 0;
                  return (
                    <div
                      key={order.id}
                      style={styles.orderCard}
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <div style={styles.orderImageContainer}>
                        {imageUrl ? (
                          <Img
                            src={imageUrl}
                            alt={order.showName ?? 'Order'}
                            variant="thumb"
                            renderedWidth={80}
                            style={styles.orderImage}
                          />
                        ) : (
                          <div style={styles.orderImagePlaceholder}>
                            <Package size={24} color={colors.gray[300]} />
                          </div>
                        )}
                      </div>
                      <div style={styles.orderDetails}>
                        <h3 style={styles.orderItemName}>{order.showName ?? 'Order'}</h3>
                        {order.sellerUsername && (
                          <div style={styles.sellerPill}>
                            <UserAvatar src={order.sellerAvatarUrl} alt={order.sellerUsername} size={20} />
                            <span style={styles.sellerName}>{order.sellerUsername}</span>
                          </div>
                        )}
                        <p style={styles.orderMeta}>Order Number: <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{order.id}</span></p>
                        <p style={styles.orderMeta}>Order Date: {formatDate(order.createdAt)}</p>
                        <p style={styles.orderMeta}>Order Total: {formatCurrency(order.totalValue)}</p>
                        <p style={styles.itemCount}>
                          <ShoppingBag size={12} />
                          <span>{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
                        </p>
                      </div>
                      <div style={styles.orderRight}>
                        <div
                          style={{
                            ...styles.statusBadge,
                            backgroundColor: statusConfig.bgColor,
                            color: statusConfig.color,
                          }}
                        >
                          <statusConfig.Icon size={12} />
                          <span>{statusConfig.label}</span>
                        </div>
                      </div>
                      <ChevronRight size={20} color={colors.gray[400]} />
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button
                    style={{ ...styles.pageButton, opacity: page === 0 ? 0.5 : 1 }}
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </button>
                  <span style={styles.pageInfo}>Page {page + 1} of {totalPages}</span>
                  <button
                    style={{ ...styles.pageButton, opacity: page >= totalPages - 1 ? 0.5 : 1 }}
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '15px',
    color: colors.gray[600],
    marginBottom: '32px',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: colors.gray[600],
  },
  error: {
    textAlign: 'center',
    padding: '48px',
    color: colors.accent.pink,
    backgroundColor: '#FEE2E2',
    borderRadius: '12px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 24px',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
  },
  emptyIcon: {
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  orderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  orderCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  orderImageContainer: {
    width: '64px',
    height: '64px',
    flexShrink: 0,
  },
  orderImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
  },
  orderImagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.gray[100],
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  orderDetails: {
    flex: 1,
    minWidth: 0,
  },
  orderItemName: {
    fontSize: '16px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sellerPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 10px 3px 3px',
    backgroundColor: colors.gray[100],
    borderRadius: '100px',
    marginBottom: '6px',
  },
  sellerAvatar: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
  },
  sellerAvatarPlaceholder: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: colors.gray[300],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 700,
    color: colors.white,
  },
  sellerName: {
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[700],
  },
  orderMeta: {
    fontSize: '13px',
    color: colors.gray[600],
    margin: '0 0 2px 0',
  },
  itemCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: colors.gray[600],
    marginTop: '4px',
  },
  orderRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 600,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '24px',
  },
  pageButton: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.brand.blue,
    backgroundColor: colors.brand.blue15,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  pageInfo: {
    fontSize: '14px',
    color: colors.gray[600],
  },
};
